import React, { lazy, Suspense } from "react";

import Upgrading from "../common/Upgrading";
import Fp404 from "../common/Fp404";
import { Route, Routes, useParams, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useOrgId from "../FpAppV4/hooks/useOrgId";
import TaskListEventBridge from "@/components/FpPages/Tasks/TaskListEventBridge";
import useWebsocketRoom from "../FpAppV4/hooks/useWebsocketRoom";
import FpReactQueryDevtools from "../FpFundBrain/FpReactQueryDevTools";
import {
  fromArtifactSlug,
  isArtifactSlug,
} from "../FpPages/Artifacts/artifactSlug";
import ListenCiteReference from "../ReferenceWrapper/ListenCiteReference";
import DocumentsUsedInQueryAnswer from "../ReferenceWrapper/DocumentsUsedInQueryAnswer";
import QueryInfoPopoverBox from "../ReferenceWrapper/QueryInfoPopoverBox";
import ShowWorkPopover from "../common/ShowWorkPopover";
import DocumentViewDrawer from "../common/DocumentViewDrawer";
import TrayViewer from "../common/FileTree/UploadStuff/TrayViewer";
import StaffIngressDialogHost from "../FpPages/StaffIngressDialog";
import NotetakerChatLifetime from "../FpPages/Notetaker/NotetakerChatLifetime";
import SkeletonLoader, { PageSkeleton } from "../FpPages/SkeletonLoader";
// Layout from its own file, never the FpPages barrel: the barrel re-exports
// every page, which would pull them all back into the entry chunk.
import Layout from "../FpPages/Layout";
import Sidebar from "../FpPages/SideBar";
import {
  AppearanceSettingsRedirect,
  ComponentLibraryRedirect,
} from "../FpPages/RMS/Components/appearanceLocation";
import { ConnectorsSettingsRedirect } from "../FpPages/Settings/connectorsLocation";
import {
  NewReportRedirect,
  NewMatrixRedirect,
} from "../FpPages/NewWorkspaceRedirect";
import { NavigationHistoryProvider } from "../common/navigation";
import RouteErrorBoundary from "./RouteErrorBoundary";
import ClaimsOnlyGate from "./ClaimsOnlyGate";
import { routeModules } from "./routeModules";
import { isClientEnvironment } from "@/services/clientEnvironment";

// Every page loads the first time its route renders (#12239), so the entry
// script carries only the shell above. The loaders live in routeModules.ts,
// where a test awaits each one.
const AdminControl = lazy(routeModules.AdminControl);
const AdminDashboardTemplateEditor = lazy(
  routeModules.AdminDashboardTemplateEditor
);
const ManagePromptTemplates = lazy(routeModules.ManagePromptTemplates);
const FilesListView = lazy(routeModules.FilesListView);
const ReviewView = lazy(routeModules.ReviewView);
const CheckReviewView = lazy(routeModules.CheckReviewView);
const FpHighlighterWithURLParams = lazy(
  routeModules.FpHighlighterWithURLParams
);
const FpHighlighterWithUUID = lazy(routeModules.FpHighlighterWithUUID);
const HomePage = lazy(routeModules.HomePage);
const AlertsPage = lazy(routeModules.AlertsPage);
const AlertEditor = lazy(routeModules.AlertEditor);
const ReportsPage = lazy(routeModules.ReportsPage);
const ReportEditor = lazy(routeModules.ReportEditor);
const ReportTemplateEditor = lazy(routeModules.ReportTemplateEditor);
const ArtifactsPage = lazy(routeModules.ArtifactsPage);
const ChatConversation = lazy(routeModules.ChatConversation);
const ChatsHub = lazy(routeModules.ChatsHub);
const SkillsPage = lazy(routeModules.SkillsPage);
const SkillDetail = lazy(routeModules.SkillDetail);
const TasksPage = lazy(routeModules.TasksPage);
const TaskListPage = lazy(routeModules.TaskListPage);
const AgentsPage = lazy(routeModules.AgentsPage);
const AgentDetail = lazy(routeModules.AgentDetail);
const AgentRunView = lazy(routeModules.AgentRunView);
const AgentChatConversation = lazy(routeModules.AgentChatConversation);
const DocumentsPage = lazy(routeModules.DocumentsPage);
const IntakePage = lazy(routeModules.IntakePage);
const ClaimsReviewPage = lazy(routeModules.ClaimsReviewPage);
const Exposures = lazy(routeModules.Exposures);
const Manage = lazy(routeModules.Manage);
const Emails = lazy(routeModules.Emails);
const ManageUsers = lazy(routeModules.ManageUsers);
const FundPortfolios = lazy(routeModules.FundPortfolios);
const ManageConnections = lazy(routeModules.ManageConnections);
const SettingsPage = lazy(routeModules.SettingsPage);
const MatrixPage = lazy(routeModules.MatrixPage);
const MatrixEditor = lazy(routeModules.MatrixEditor);
const MatrixTemplateEditor = lazy(routeModules.MatrixTemplateEditor);
const RMSPage = lazy(routeModules.RMSPage);
const SetupPage = lazy(routeModules.SetupPage);
const InteractionPage = lazy(routeModules.InteractionPage);
const EntityDashboard = lazy(routeModules.EntityDashboard);
const ComponentBuilderRoute = lazy(routeModules.ComponentBuilderRoute);
const NotetakerIndexPage = lazy(routeModules.NotetakerIndexPage);
const NotetakerInstallWalkthroughPage = lazy(
  routeModules.NotetakerInstallWalkthroughPage
);
const NotetakerMeetingPage = lazy(routeModules.NotetakerMeetingPage);
const NotetakerChatHistoryPage = lazy(routeModules.NotetakerChatHistoryPage);
const NotetakerChatViewPage = lazy(routeModules.NotetakerChatViewPage);
const CRMPage = lazy(routeModules.CRMPage);
const AssetSummaryWrapper = lazy(routeModules.AssetSummaryWrapper);
const AssetMeetingsNotesWrapper = lazy(routeModules.AssetMeetingsNotesWrapper);
const ManagerView = lazy(routeModules.ManagerView);
const ManagerMeetingsNotes = lazy(routeModules.ManagerMeetingsNotes);
const ReportsWrapper = lazy(routeModules.ReportsWrapper);
const TaskOpenBridge = lazy(routeModules.TaskOpenBridge);
const RmsHomePrefetch = lazy(routeModules.RmsHomePrefetch);

// /artifacts/{id} carries two id shapes (#11548 r27): a report-editor
// workspace id (a Mongo ObjectId) opens the full editor (unchanged); an
// artifact — a bare uuid since #11819, or the prefixed id older links carry
// — opens the library with that artifact opened in place.
const ArtifactIdOrEditor = () => {
  const { workspaceId } = useParams();
  return workspaceId && isArtifactSlug(workspaceId) ? (
    <ArtifactsPage openArtifactId={fromArtifactSlug(workspaceId)} />
  ) : (
    <ReportEditor />
  );
};

// Artifact conversations moved into MAIN agentic chat; old
// /artifacts/chat/:id links land on the same conversation there.
const ArtifactChatRedirect = () => {
  const { workspaceId } = useParams();
  return <Navigate to={`/chat/${workspaceId}`} replace />;
};

const MainApp = () => {
  const orgId = useOrgId();
  const hasRms = useSelector(
    (state) =>
      state.userData?.organization?.allowed_products?.includes("crm") === true
  );
  const isStaff = useSelector((state) => state.userData?.isStaff === true);
  useWebsocketRoom("organization", orgId || null);
  // The staff console belongs to the staff member's own app (#10718). Inside
  // a client environment its routes are not mounted, so the not-found page
  // answers and no console request is ever made as the collaborator. On a
  // home tab they are mounted for a staff account only: a client's admin who
  // types a console address gets the not-found page, not the console's shell
  // (the template pages had no gate of their own; #10718 verification,
  // 2026-10-22).
  const hasStaffConsole = !isClientEnvironment() && isStaff;

  return (
    <>
      <FpReactQueryDevtools />
      <ListenCiteReference />
      <DocumentsUsedInQueryAnswer />
      <QueryInfoPopoverBox />
      <ShowWorkPopover />
      <DocumentViewDrawer />
      <TrayViewer />
      <StaffIngressDialogHost />
      {/* Ends the Meetings chat once the reader leaves the Notetaker. */}
      <NotetakerChatLifetime />
      {/* RMS Home, prefetched once the app is idle and on intent (#11849). Renders nothing,
          and its chunk loads only for an organization with RMS. */}
      {hasRms && (
        <Suspense fallback={null}>
          <RmsHomePrefetch />
        </Suspense>
      )}

      <NavigationHistoryProvider>
        {/* Catches the admin and review pages, which render their own Layout
            outside the in-app page area below. */}
        <Suspense fallback={<SkeletonLoader />}>
          <Routes>
            <Route path="/upgrading" element={<Upgrading />} />
            {hasStaffConsole && (
              <>
                <Route
                  path="/__admin"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/notetaker"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/rms-findings"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/models"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/analytics"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/costs"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/organization-templates"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminControl />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/templates"
                  element={<ManagePromptTemplates />}
                />
                <Route
                  path="/__admin/templates/dashboards/new"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminDashboardTemplateEditor />
                    </Layout>
                  }
                />
                <Route
                  path="/__admin/templates/dashboards/:publicId"
                  element={
                    <Layout>
                      <Sidebar />
                      <AdminDashboardTemplateEditor />
                    </Layout>
                  }
                />
              </>
            )}

            <Route path="review">
              <Route
                index
                element={
                  <Layout>
                    <Sidebar />
                    <FilesListView />
                  </Layout>
                }
              />
              <Route
                path="artifact/:artifactId"
                element={
                  <Layout>
                    <Sidebar />
                    <ReviewView />
                  </Layout>
                }
              />
              <Route
                path="check/:taskId"
                element={
                  <Layout>
                    <Sidebar />
                    <CheckReviewView />
                  </Layout>
                }
              />
            </Route>

            <Route
              path="*"
              element={
                <Layout>
                  <Sidebar />
                  <TaskListEventBridge />
                  {/* No placeholder: the drawer shows nothing until a task is
                      opened. */}
                  <Suspense fallback={null}>
                    <TaskOpenBridge />
                  </Suspense>
                  {/* A page that is still loading shows its skeleton in the
                    page area, inside the loaded shell; one that fails to load
                    is contained here instead of replacing the whole app. The
                    boundary sits outside Suspense so it catches the rejected
                    import. */}
                  <RouteErrorBoundary>
                    <Suspense fallback={<PageSkeleton />}>
                      {/* A claims-only organization lands on /claims from any
                          route it cannot use (#11422). */}
                      <ClaimsOnlyGate>
                        <Routes>
                          <Route
                            path="/tools/fp-highlighter"
                            element={<FpHighlighterWithURLParams />}
                          />
                          <Route index element={<HomePage />} />

                          <Route path="alerts">
                            <Route index element={<AlertsPage />} />
                            <Route
                              path="editor/new"
                              element={<AlertEditor />}
                            />
                            <Route
                              path="editor/:alertId"
                              element={<AlertEditor />}
                            />
                            <Route
                              path=":alertId/:invocationId"
                              element={<FpHighlighterWithUUID />}
                            />
                          </Route>
                          <Route path="reports">
                            <Route index element={<ReportsPage />} />
                            <Route path="new" element={<NewReportRedirect />} />
                            <Route
                              path=":workspaceId"
                              element={<ReportEditor />}
                            />
                          </Route>
                          <Route path="artifacts">
                            <Route index element={<ArtifactsPage />} />
                            {/* Artifact conversations live in MAIN agentic chat now;
                        this redirect keeps links minted while the Artifacts
                        surface hosted its own chat working. Declared before
                        the editor route so "chat" isn't read as a workspace
                        id. */}
                            <Route
                              path="chat/:workspaceId"
                              element={<ArtifactChatRedirect />}
                            />
                            {/* Editor-backed artifacts open the report editor here,
                        NOT under /reports — Artifacts is its own surface
                        (#11523). The editor reads the same :workspaceId
                        param and stays on whatever pathname hosts it.
                        Artifact IDS (pgid-org-artifacts-…, the shape email
                        deep links carry, #11548 r27) land on the library
                        instead, which opens the artifact itself. */}
                            <Route
                              path=":workspaceId"
                              element={<ArtifactIdOrEditor />}
                            />
                          </Route>
                          <Route path="report-templates">
                            <Route
                              index
                              element={<Navigate to="/reports" replace />}
                            />
                            <Route
                              path=":templateId"
                              element={<ReportTemplateEditor />}
                            />
                          </Route>
                          <Route path="chat">
                            <Route index element={<ChatConversation />} />
                            <Route
                              path=":workspaceId"
                              element={<ChatConversation />}
                            />
                          </Route>
                          {/* The phone Chats hub (#11742 round 3): chat's collection
                      surface. Redirects to the starter at desktop widths. */}
                          <Route path="chats" element={<ChatsHub />} />
                          <Route path="skills">
                            <Route index element={<SkillsPage />} />
                            <Route path=":skillId" element={<SkillDetail />} />
                          </Route>
                          {/* Tasks (#11942 prototype): the viewer's tasks, the
                      lists they belong to. */}
                          <Route path="tasks">
                            <Route index element={<TasksPage />} />
                            <Route
                              path="lists/:listId"
                              element={<TaskListPage />}
                            />
                            <Route path=":tab" element={<TasksPage />} />
                          </Route>
                          <Route path="agents">
                            <Route index element={<AgentsPage />} />
                            {/* The agent's tab is a path segment (#12006):
                        /runs, /agent, /automation, /artifacts. */}
                            <Route
                              path=":agentId/:tab?"
                              element={<AgentDetail />}
                            />
                            <Route
                              path=":agentId/runs/:runId"
                              element={<AgentRunView />}
                            />
                            <Route
                              path=":agentId/runs/:runId/conversations/:conversationId"
                              element={<AgentRunView />}
                            />
                            <Route
                              path=":agentId/sessions/:sessionId"
                              element={<AgentChatConversation />}
                            />
                          </Route>
                          <Route path="documents" element={<DocumentsPage />} />
                          {/* BCBSWY mailroom claims review queue (#11422). Reachable
                      only for an org whose allowed_products carries
                      bcbswy_intake; every route behind it 404s for anyone
                      else. */}
                          <Route path="claims" element={<IntakePage />} />
                          {/* The review screen (#11422): a leaf under the queue. */}
                          <Route
                            path="claims/:itemId"
                            element={<ClaimsReviewPage />}
                          />
                          {/* Legacy (#12120): the install page is /notetaker itself
                      now; this only keeps old links working. */}
                          <Route
                            path="notetaker-install"
                            element={<Navigate to="/notetaker" replace />}
                          />
                          <Route path="exposures" element={<Exposures />} />
                          <Route path="manage">
                            <Route index element={<Manage />} />
                            <Route path="emails" element={<Emails />} />
                            <Route path="users" element={<ManageUsers />} />
                            <Route
                              path="fund-portfolios"
                              element={<FundPortfolios />}
                            />
                            {/* Fund Tags moved to Funds page - accessible to all users with funds access */}
                            {/* <Route path="fund-tags" element={<FundTags />} /> */}
                            <Route
                              path="connections"
                              element={<ManageConnections />}
                            />
                          </Route>
                          <Route path="settings">
                            {/* Settings lands on the Overview map (gaps-v8 §3.1),
                        not on Users. */}
                            <Route index element={<SettingsPage />} />
                            <Route
                              path="appearance"
                              element={<SettingsPage tab="appearance" />}
                            />
                            {/* Appearance's old address (#13370), for links and bookmarks. */}
                            <Route
                              path="components"
                              element={<AppearanceSettingsRedirect />}
                            />
                            <Route
                              path="users"
                              element={<SettingsPage tab="users" />}
                            />
                            <Route
                              path="logs"
                              element={<SettingsPage tab="emails" />}
                            />
                            <Route
                              path="interactions"
                              element={<SettingsPage tab="interactions" />}
                            />
                            <Route
                              path="connectors"
                              element={<SettingsPage tab="connections" />}
                            />
                            {/* The calendar's old addresses (#12394, round 26): a sign-in
                                started before the move still returns there. */}
                            <Route
                              path="connected-accounts"
                              element={<ConnectorsSettingsRedirect />}
                            />
                            <Route
                              path="calendar"
                              element={<ConnectorsSettingsRedirect />}
                            />
                            <Route
                              path="rms"
                              element={<SettingsPage tab="rms" />}
                            />
                            <Route
                              path="rms/:subtab"
                              element={<SettingsPage tab="rms" />}
                            />
                            <Route
                              path="notetaker"
                              element={<SettingsPage tab="notetaker" />}
                            />
                            <Route
                              path="metrics"
                              element={<SettingsPage tab="metrics" />}
                            />
                            <Route
                              path="access"
                              element={<SettingsPage tab="access" />}
                            />
                            <Route
                              path="usage"
                              element={<SettingsPage tab="usage" />}
                            />
                            {/* The usage dashboard's drill-ins (#13953), its
                        kind pages and its users page (#14755); the rail keeps
                        Usage selected on them. */}
                            <Route
                              path="usage/:usagePage"
                              element={<SettingsPage tab="usage" />}
                            />
                            <Route
                              path="usage/users/:userId"
                              element={<SettingsPage tab="usage" />}
                            />
                            <Route
                              path="usage/agents/:agentId"
                              element={<SettingsPage tab="usage" />}
                            />
                            <Route
                              path="usage/kinds/:kind"
                              element={<SettingsPage tab="usage" />}
                            />
                            <Route
                              path="mcp-keys"
                              element={<SettingsPage tab="mcp-keys" />}
                            />
                            <Route
                              path="agent-memory"
                              element={<SettingsPage tab="agent-memory" />}
                            />
                            <Route
                              path="fund-portfolios"
                              element={<SettingsPage tab="fund-portfolios" />}
                            />
                            {/* The Enterprise API reference (#14772). */}
                            <Route
                              path="api"
                              element={<SettingsPage tab="api" />}
                            />
                          </Route>
                          <Route path="matrix">
                            <Route index element={<MatrixPage />} />
                            <Route path="new" element={<NewMatrixRedirect />} />
                            <Route
                              path=":workspaceId"
                              element={<MatrixEditor />}
                            />
                          </Route>
                          <Route path="matrix-templates">
                            <Route
                              path=":workspaceId"
                              element={<MatrixTemplateEditor />}
                            />
                          </Route>
                          <Route path="rms">
                            {/* The library lives in Settings; its old address still works. */}
                            <Route
                              path="components"
                              element={<ComponentLibraryRedirect />}
                            />
                            <Route
                              path="components/builders/:builderId"
                              element={<ComponentBuilderRoute />}
                            />
                            <Route index element={<RMSPage />} />
                            {/* Home's old address (#11849): the pinned views took its place (#13052). */}
                            <Route
                              path="home"
                              element={<Navigate to="/rms" replace />}
                            />
                            {/* Dashboards: the list, and one dashboard by its view's public id. */}
                            <Route
                              path="dashboards"
                              element={<RMSPage dashboards />}
                            />
                            <Route
                              path="dashboards/:dashboardId"
                              element={<RMSPage dashboards />}
                            />
                            <Route
                              path="view/:viewSlug"
                              element={<RMSPage />}
                            />
                            <Route path="settings" element={<SetupPage />} />
                            {/* Interaction create page */}
                            <Route
                              path="interactions/create"
                              element={<InteractionPage create />}
                            />
                            {/* Interaction detail page */}
                            <Route
                              path="interaction/:interactionId"
                              element={<InteractionPage />}
                            />
                            {/* Entity detail pages */}
                            <Route
                              path=":entityKind/:entityId"
                              element={<EntityDashboard />}
                            />
                            <Route
                              path=":entityKind/:entityId/:panelKind"
                              element={<EntityDashboard />}
                            />
                            <Route
                              path=":entityKind/:entityId/:panelKind/:subParam"
                              element={<EntityDashboard />}
                            />
                            <Route
                              path="*"
                              element={<Navigate to="/rms" replace />}
                            />
                          </Route>
                          {/* Notetaker - standalone meeting view for orgs without RMS */}
                          <Route path="notetaker">
                            <Route index element={<NotetakerIndexPage />} />
                            <Route
                              path="install"
                              element={<NotetakerInstallWalkthroughPage />}
                            />
                            <Route
                              path="meeting/:interactionId"
                              element={<NotetakerMeetingPage />}
                            />
                            <Route
                              path="chat"
                              element={<NotetakerChatHistoryPage />}
                            />
                            <Route
                              path="chat/:workspaceId"
                              element={<NotetakerChatViewPage />}
                            />
                          </Route>
                          <Route path="crm">
                            <Route
                              index
                              element={<Navigate to="/crm/assets" replace />}
                            />
                            <Route path="assets" element={<CRMPage />} />
                            <Route path="reports" element={<CRMPage />} />
                            <Route path="templates" element={<CRMPage />} />
                            <Route path="assets/:assetId">
                              <Route
                                path="summary"
                                element={<AssetSummaryWrapper />}
                              />
                              <Route
                                path="meetings-notes"
                                element={<AssetMeetingsNotesWrapper />}
                              />
                            </Route>
                            <Route path="managers">
                              <Route path=":managerId">
                                <Route index element={<ManagerView />} />
                                <Route
                                  path="meetings-notes"
                                  element={<ManagerMeetingsNotes />}
                                />
                              </Route>
                            </Route>
                            <Route path="reports/:reportId">
                              <Route index element={<ReportsWrapper />} />
                              <Route
                                path="executed-reports/:executedReportId"
                                element={<ReportsWrapper />}
                              />
                            </Route>
                          </Route>

                          <Route path="*" element={<Fp404 />} />
                        </Routes>
                      </ClaimsOnlyGate>
                    </Suspense>
                  </RouteErrorBoundary>
                </Layout>
              }
            ></Route>
          </Routes>
        </Suspense>
      </NavigationHistoryProvider>
    </>
  );
};

export default MainApp;
